/**
 * Taxonomy API module.
 *
 * Public endpoints — no Authorization header required. Wraps the two
 * taxonomy endpoints added by PL-TAX-003 and consumed by SF-NAV-001.
 *
 *   GET /v1/taxonomies/products/cascade  (Track B — product configurator cascade)
 *   GET /v1/taxonomies/:slug/path/:path
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  TaxonomyCascadeDTO,
  TaxonomyPathDTO,
} from '@dfq/shared/types/taxonomy';

/**
 * Fetch the product cascade — categories, materials, and vehicle surfaces.
 *
 * Calls `GET /taxonomies/products/cascade` (Track B endpoint, PR #832).
 * Returns `{categories, materials, vehicleSurfaces}` — the three configurator
 * dimensions for the storefront home and navigation mega-menu.
 *
 * The `slug` parameter is kept for API-surface compatibility; only `"products"`
 * resolves to the new endpoint — other slugs fall through to the generic
 * wildcard route which returns a different shape.
 */
export async function getCategoryCascade(slug: string): Promise<TaxonomyCascadeDTO> {
  try {
    const response = await apiClient.get<ApiEnvelope<{ cascade: TaxonomyCascadeDTO }>>(
      `/taxonomies/${encodeURIComponent(slug)}/cascade`,
    );
    return unwrap(response.data, response.status).cascade;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * Resolve a multi-segment path within a taxonomy to its node and
 * ancestor chain.
 *
 * Path segments are slash-separated (e.g. `"vehicle-wraps/cars"`).
 * The resolved node and its ancestors are returned together so the
 * category browse page can render breadcrumbs without a second round-trip.
 *
 * Throws an `ApiError` with `status: 404` when the path does not exist
 * or the node at that path is inactive — callers should navigate to the
 * 404 page in that case.
 */
export async function getCategoryByPath(
  slug: string,
  path: string,
): Promise<TaxonomyPathDTO> {
  try {
    const response = await apiClient.get<ApiEnvelope<{ resolved: TaxonomyPathDTO }>>(
      `/taxonomies/${encodeURIComponent(slug)}/path/${path}`,
    );
    return unwrap(response.data, response.status).resolved;
  } catch (err) {
    throwAsApiError(err);
  }
}

export const taxonomiesApi = {
  getCategoryCascade,
  getCategoryByPath,
};
